<template>
  <div class="photoslider">
    <div class="slider">
      <transition
        @enter="enter"
        @leave="leave"
      >
        <figure
          class="slide__cont"
          v-for="number in [currentNumber]"
          :key="number"
        >
          <img
            v-lazy="images[Math.abs(currentNumber) % images.length].url"
            v-if="isFirst"
          />
          <div
            class="slider__image"
            v-lazy:background-image="`${images[Math.abs(currentNumber) % images.length].url}${extension}`"
            v-if="isFirst"
          ></div>
          <img
            :src="`${images[Math.abs(currentNumber) % images.length].url}${extension}`"
            v-if="!isFirst"
          />
          <div
            class="slider__image"
            :style="{ backgroundImage: `url(${images[Math.abs(currentNumber) % images.length].url}${extension})` }"
            v-if="!isFirst"
          ></div>
        </figure>
      </transition>
    </div>

    <div class="slider-navigator">
      <button class="slider-navigator__button slider-navigator__button--prev"
        data-mouse
        data-mouse-attractionState="disactive"
        data-hover-type="fadeout"
        @click="prev"
      >
        prev
      </button><!--
      --><button class="slider-navigator__button slider-navigator__button--next"
          data-mouse
          data-mouse-attractionState="disactive"
          data-hover-type="fadeout"
          @click="next"
        >
          next
      </button>
      <span class="slider-navigator__line"></span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import gsap from 'gsap';
import Hammer from 'hammerjs';

import { isMobile } from '@/scripts/utils';

export default {
  name: 'PhotoSlider',

  data() {
    return {
      currentNumber: 0,
      images: [],
      extension: '.jpg',
      isFirst: true,
      direction: null,
      isMobile,
    };
  },

  computed: {
    ...mapGetters(['getDataCityComponent']),
  },

  created() {
    this.images = this.getDataCityComponent(this.$route.params.city, 'photoslider');

    this.extension = this.switchExtension();
  },

  mounted() {
    this.hammer = new Hammer(this.$el.querySelector('.slider'));

    this.bindEvents();
  },

  methods: {
    next() {
      if (this.isFirst) {
        gsap.set('.slider', { backgroundColor: 'black' });
        this.isFirst = false;
      }

      this.currentNumber += 1;
      this.direction = 'next';
    },

    bindEvents() {
      if (this.isMobile()) this.hammer.on('swiperight', this.next.bind(this));
      if (this.isMobile()) this.hammer.on('swipeleft', this.prev.bind(this));
    },

    unbindEvents() {
      if (this.isMobile()) this.hammer.off('swiperight');
      if (this.isMobile()) this.hammer.off('swipeleft');
    },

    prev() {
      if (this.isFirst) {
        gsap.set('.slider', { backgroundColor: 'black' });
        this.isFirst = false;
      }

      this.currentNumber -= 1;
      this.direction = 'prev';
    },

    enter(el, done) {
      const slide = el.querySelector('.slider__image');
      const clip = this.direction === 'next' ? 'inset(0% 100% 0.01% 0%)' : 'inset(0% 0.01% 0% 100%)';

      gsap.fromTo(el, 0.5, {
        webkitClipPath: clip,
        clipPath: clip,
      }, {
        webkitClipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
        clipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
        clearProps: 'all',
        delay: 0.2,
        onComplete: done,
      });

      gsap.from(slide, 0.5, { autoAlpha: 0, scale: 1.3, delay: 0.1 });
    },

    leave(el, done) {
      const slide = el.querySelector('.slider__image');
      const clip = this.direction === 'prev' ? 'inset(0% 100% 0% 0.01%)' : 'inset(0% 0.01% 0% 100%)';

      gsap.fromTo(el, 0.5, {
        webkitClipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
        clipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
      }, {
        webkitClipPath: clip,
        clipPath: clip,
        clearProps: 'all',
        onComplete: done,
      });

      gsap.to(slide, 0.5, { autoAlpha: 0, scale: 0.9, delay: 0.1 });
    },

    switchExtension() {
      if ((window.chrome && window.chrome.webstore) || (window.opr && window.opr.addons)) {
        return '.webp';
      }
      return '.jpg';
    },
  },

  beforeDestroy() {
    this.unbindEvents();
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/colors';
@import '../application';

.photoslider {
  position: relative;
  padding: 0;
  margin: 100px 0 40px 0;

  display: flex;
  align-items: center;
  flex-wrap: wrap;

  @include mq(sm) {
    margin: 100px 0;
  }

  &::before {
    @include mq(sm) {
      content: '';
      position: absolute;
      width: calc(30% - 120px);
      height: 1px;
      top: 50%;
      left: calc(70% + 160px);

      background-color: $color-blue;
    }
  }

  .slider {
    display: inline-block;
    position: relative;
    width: 100%;
    margin: 0 0 40px 0;
    background-color: transparent;

    @include aspect-ratio(3, 2);

    overflow: hidden;

    @include mq(sm) {
      width: 70%;
      margin: 0 0 0 -40px;
    }

    img {
      @include visually-hidden;
    }

    .slide__cont {
      display: inline-block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      margin: 0;

      @include boost-performance;
    }

    &__image {
      display: inline-block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;
    }
  }

  .slider-navigator {
    display: inline-block;
    position: relative;
    margin-left: 20px;


    @include mq(sm) {
      margin-left: 40px;
    }

    &__button {
      position: relative;
      padding: 10px 20px;
      margin: 0;

      background: transparent;
      border: 0;

      font-weight: 500;

      cursor: pointer;
      z-index: 1;

      &::before {
        content: '';

        display: inline-block;
        position: absolute;
        top: 0;
        width: 100%;
        height: 100%;

        background-color: $color-blue;

        transform: scaleX(0);
        transition: all 0.6s cubic-bezier(.17,.67,.32,.87);

        z-index: -1;
      }

      @at-root .pointerevents:not(.touchevents) &:hover::before,
      .no-pointerevents.no-touchevents &:hover::before {
        transform: scale(1);
      }

      &:focus {
        outline: transparent;
      }
    }

    &__button--prev {
      &::before {
        right: 0;
        transform-origin: right center;
      }
    }

    &__button--next {
      &::before {
        left: 0;
        transform-origin: left center;
      }
    }

    &__line {
      display: inline-block;
      position: absolute;
      top: 0;
      left: 50%;
      width: 8px;
      height: 100%;
      margin-left: -4px;

      background-color: $color-blue;

      transform: scaleX(1);
      transform-origin: center center;
      transition: all 0.6s cubic-bezier(.17,.67,.32,.87);
    }

    @at-root .pointerevents:not(.touchevents) &:hover .slider-navigator__line,
    .no-pointerevents.no-touchevents &:hover .slider-navigator__line {
      transform-origin: center center;
      transform: scaleX(0);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PhotoSlider.vue